import { t } from 'i18next'
import { z } from 'zod'

import { VagaValidator } from '@/features/proposta/brasil/domain/InformacoesIniciaisStep/vaga-validator'
import { ValidadorComprador } from '@/features/proposta/brasil/domain/ParticipacaoComprador/participacao-comprador-validador'
import { PropostaDtoMapper } from '@/features/proposta/brasil/domain/PropostaMapper/proposta-dto-mapper'
import {
  AssinaturaContratoSchema,
  ImobiliariaCorretoresSchema,
} from '@/features/proposta/brasil/domain/schemas/detalhes-proposta-schema'
import {
  simulacaoPropostaBrasilSchema,
  type TSimulacaoPropostaBrasil,
} from '@/features/proposta/brasil/domain/schemas/simulacao-proposta-brasil.schema'
import type { TPropostaData } from '@/features/proposta/brasil/types/proposta.type'
import { PlantaValidator } from '@/features/proposta/shared/domain/InformacoesIniciaisStep/planta-validator'
import { VendedorValidator } from '@/features/proposta/shared/domain/InformacoesIniciaisStep/vendedor-validator'
import { EstadoCivilEnum, tipoDeCliente } from '@/types/cliente'
import { ClienteRegimeCasamentoEnum, opcaoVagaEnum, tipoAssinaturaEnum, tipoDoCompradorEnum } from '@/types/proposta'
import { origemTelefone } from '@/utils/schemas/telefone.schema'

const informacoesIniciaisSchema = z
  .object({
    idExternoBloco: z.number().readonly(),
    vendedorAlocadoCliente: z.string().nullish().readonly(),
    clienteAtendimento: z.object({
      id: z.string(),
      cpf: z.string().nullish(),
    }),
    simulacaoUnidade: simulacaoPropostaBrasilSchema.nullish(),
    simulacaoLote: simulacaoPropostaBrasilSchema.nullish(),
    simulacoesAcessorios: z.array(simulacaoPropostaBrasilSchema),
    opcaoVaga: z.enum(opcaoVagaEnum).nullish(),
    vagaIds: z.array(
      z.object({
        id: z.number(),
        nomeVaga: z.string(),
        descricao: z.string(),
        substituiVagaPadrao: z.boolean().default(false),
      }),
    ),
    planta: z.object({
      id: z.string(),
      descricao: z.string(),
    }),
    vendedor: z
      .object({
        id: z.string(),
        possuiDadosDeComissao: z.boolean(),
      })
      .superRefine(({ id, possuiDadosDeComissao }, ctx) => {
        const vendedorError = new VendedorValidator({
          vendedorId: id,
          possuiDadosDeComissao: possuiDadosDeComissao,
        }).validate()
        if (vendedorError) {
          ctx.addIssue({
            code: 'custom',
            message: vendedorError,
            input: { id, possuiDadosDeComissao },
          })
        }
      }),
  })
  .superRefine(({ simulacaoUnidade, simulacoesAcessorios, planta, vagaIds }, ctx) => {
    const plantaError = new PlantaValidator({
      isUnidade: !!simulacaoUnidade,
      plantaId: planta.id,
    }).validate()

    const vagaError = new VagaValidator<TSimulacaoPropostaBrasil>({
      simulacaoUnidade: simulacaoUnidade,
      vagaIds: vagaIds,
      possuiAcessoriosExtras: !!simulacoesAcessorios.length,
    }).validate()

    if (plantaError) {
      ctx.addIssue({
        path: ['planta'],
        code: 'custom',
        message: plantaError,
      })
    }

    if (vagaError) {
      ctx.addIssue({
        path: ['vagaIds'],
        code: 'custom',
        message: vagaError,
      })
    }
  })

export const participacoesCompradoresSchema = z
  .object({
    titular: z.object({
      id: z.string(),
      percentualParticipacao: z.number(),
    }),
    compradores: z.array(
      z.object({
        id: z.string(),
        percentualParticipacao: z
          .number()
          .min(0.01, t('proposta:detalhes_proposta.participacao_comprador.erros.maior_zero'))
          .max(99.99, t('proposta:detalhes_proposta.participacao_comprador.erros.menor_cem')),
      }),
    ),
  })
  .superRefine(({ compradores, titular }, ctx) => {
    if (!compradores.length) return
    const erros = ValidadorComprador.validarParticipacoesCompradores(titular, compradores)
    if (!erros.length) return
    for (const erro of erros) {
      ctx.addIssue({ code: 'custom', message: erro.message, path: erro.path })
    }
  })

const vinculoSchema = z.object({
  clienteId: z.string(),
  tipo: z.enum(['Representante', 'MaePai', 'Conjuge']),
})

export const compradorSchema = z.object({
  clienteId: z.string(),
  tipo: z.enum(tipoDoCompradorEnum),
  vinculos: z.array(vinculoSchema),
  tipoAssinatura: z.enum(tipoAssinaturaEnum).nullish(),
})

export const clientePessoaFisicaPropostaSchema = z.object({
  id: z.string(),
  tipoDoCliente: z.enum(tipoDeCliente),
  nome: z.string(),
  cpf: z.string(),
  email: z.string(),
  maiorDe18Anos: z.boolean(),
  nacionalidadeId: z.string(),
  profissaoId: z.string(),
  regimeCasamento: z.enum(ClienteRegimeCasamentoEnum).nullish(),
  estadoCivil: z.enum(EstadoCivilEnum).nullish(),
  imagem: z.string().nullish(),
  possuiUniaoEstavel: z.boolean().nullish(),
  telefone: z.object({
    numero: z.string(),
    principal: z.boolean(),
    origem: z.enum(origemTelefone),
  }),
})
export type TClientePessoaFisicaPropostaSchema = z.infer<typeof clientePessoaFisicaPropostaSchema>

export const clientePessoaJuridicaPropostaSchema = z.object({
  id: z.string(),
  tipo: z.literal(tipoDoCompradorEnum.PessoaJuridica),
  razaoSocial: z.string(),
  cnpj: z.string(),
  email: z.string(),
  telefone: z.object({
    numero: z.string(),
    principal: z.boolean(),
    origem: z.enum(origemTelefone),
  }),
})
export type TClientePessoaJuridicaPropostaSchema = z.infer<typeof clientePessoaJuridicaPropostaSchema>

export const procuradorPropostaSchema = z.object({
  procuradorId: z.string(),
  clientesIds: z.array(z.string()),
})

export type TProcuradorPropostaSchema = z.infer<typeof procuradorPropostaSchema>

export type TCompradorPropostaSchema = z.infer<typeof compradorSchema>
export type TCompradorPessoaJuridicaPropostaSchema = z.infer<typeof clientePessoaJuridicaPropostaSchema>

export const propostaSchema = z
  .object({
    step: z.number(),
    informacoesIniciais: informacoesIniciaisSchema,
    cadastroCompradores: z
      .object({
        clientes: z.array(clientePessoaFisicaPropostaSchema),
        clientesJuridicos: z.array(clientePessoaJuridicaPropostaSchema),
        compradores: z.array(compradorSchema),
        procuradores: z.array(procuradorPropostaSchema),
      })
      .superRefine(({ compradores }, ctx) => {
        if (!compradores.length) {
          ctx.addIssue({
            code: 'custom',
            message: t('proposta:cadastro_compradores.erro.obrigatorio'),
          })
        }
      }),
    detalhesDaProposta: z.object({
      participacaoCompradores: participacoesCompradoresSchema,
      imobiliariasCorretores: ImobiliariaCorretoresSchema,
      assinaturaContrato: AssinaturaContratoSchema,
      termosDaProposta: z.string().min(1),
      observacoes: z.string().optional(),
      tipoEmolumento: z.enum(['AlienacaoFiduciaria', 'ContratoCompraVenda']).nullish(),
    }),
  })
  .superRefine(({ detalhesDaProposta, cadastroCompradores: { compradores } }, ctx) => {
    if (compradores?.length <= 1) return
    const titular = detalhesDaProposta.participacaoCompradores.titular
    const erros = [
      ValidadorComprador.validarTitularObrigatorio(titular.id, [
        'detalhesDaProposta',
        'participacaoCompradores',
        'titular',
        'id',
      ]),
      ValidadorComprador.validarPercentualTitular(titular.percentualParticipacao, [
        'detalhesDaProposta',
        'participacaoCompradores',
        'titular',
        'percentualParticipacao',
      ]),
    ]
    for (const erro of erros) {
      if (!erro) continue
      ctx.addIssue({ code: 'custom', message: erro.message, path: erro.path })
    }
  })
  .transform((data) => {
    const dto: TPropostaData = new PropostaDtoMapper(data).mapear()
    return dto
  })

export type TPropostaSchemaInput = z.input<typeof propostaSchema>
export type TPropostaSchemaOutput = z.output<typeof propostaSchema>

export type TParticipacao = Extract<
  TPropostaSchemaInput['detalhesDaProposta']['imobiliariasCorretores'],
  { incluirParticipacoes: 'Sim' }
>['participacoes'][number]

export type TCorretagem = Extract<
  TPropostaSchemaInput['detalhesDaProposta']['imobiliariasCorretores'],
  { incluirParticipacoes: 'Sim' }
>['participacoes'][number]['corretagem']
